// ── V147: 중국어 화면의 홈 — 중국 매장 직원용 '工作台' ─────────────────────────────
// 원래 홈(Dashboard)은 사장님용 경영 화면이라 한국 세무·인건비·공동구매 같은 한국 매장 내용이 많고
// 글자도 한국어뿐이다. 중국어로 볼 때는 직원이 매일 쓰는 것만 먼저 보여준다.
//   · 오늘 날짜 · 매장 이름
//   · 订货提醒 — 재고 기준으로 발주가 필요한 품목 (한 번에 발주서로)
//   · 快捷入口 — 이 계정이 볼 수 있는 탭만
//   · 最近的订货单 — 최근 저장한 발주서 5장
//   · 経营数据 — 'profit'(운영) 권한이 있으면 원래 홈(한국어)을 열 수 있다
import React, { useMemo, useState } from 'react';
import { useLang } from '../i18n/LangContext';
import { buildZhMap, zhOf } from '../i18n/BiName';
import { hasHangul, unitToZh } from '../i18n/units';
import { calcAvgUsage, mergeUsage, getOrderRecommendations } from '../utils/inventoryAlerts';
import { calcRecipeBasedUsage } from '../utils/recipeUsage';
import Dashboard from './Dashboard';

const WEEK_ZH = ['日', '一', '二', '三', '四', '五', '六'];

const TILES = [
  { id: 'purchase',    icon: '📦', label: '订货单',   desc: '点食材 · 填数量 · 发微信' },
  { id: 'receiving',   icon: '📥', label: '入库登记', desc: '对着送货单 · 记实收数量 · 赊账' },   // V158
  { id: 'prices',      icon: '💰', label: '单价表',   desc: '食材进货价格 · 规格 · 供应商' },
  { id: 'recipes',     icon: '📋', label: '配方',     desc: '每道菜用什么 · 用多少' },
  { id: 'work',        icon: '✅', label: '今日工作', desc: '今天要做的 · 打勾 · 保存记录' },   // V149
  { id: 'inventory',   icon: '🗃️', label: '库存管理', desc: '现有库存 · 建议订货量' },
  { id: 'purchaseLog', icon: '🧾', label: '食材',     desc: '本月进货的食材' },
  { id: 'manual',      icon: '🖨️', label: '门店手册', desc: '做法 · 价格表 · 工作流程打印' },
  { id: 'settings',    icon: '⚙️', label: '设置',     desc: '语言 · 版本 · 账号' },
];

export function StaffHome({ prices = [], inventory = {}, orderHistory = [], categories = [], payrollSettings = {},
                            storeName = '', tabs = [], onNavigate, onAddToPurchaseOrder, onShowFull,
                            workToday = null, workChecks = {} }) {
  const { fmt } = useLang();
  const zhMap = useMemo(() => buildZhMap(prices), [prices]);
  const zhName = (ko) => zhOf(zhMap, ko) || ko;
  const vendorZh = useMemo(() => {
    const m = {};
    for (const p of prices || []) {
      const v = String(p?.vendor || '').trim(); const z = String(p?.vendor_zh || '').trim();
      if (v && z && !hasHangul(z) && !m[v]) m[v] = z;
    }
    return m;
  }, [prices]);
  const vName = (v) => vendorZh[v] || v || '未指定供应商';

  const recs = useMemo(() => {
    try {
      const usage = mergeUsage(calcAvgUsage(orderHistory || [], 3),
        calcRecipeBasedUsage(categories || [], prices || [], payrollSettings?.expectedMonthlyServings));
      return getOrderRecommendations(prices || [], inventory || {}, orderHistory || [], usage, { limit: 8 });
    } catch (e) { return []; }
  }, [prices, inventory, orderHistory, categories, payrollSettings]);

  const recent = useMemo(() => [...(orderHistory || [])]
    .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')) || String(b.id || '').localeCompare(String(a.id || '')))
    .slice(0, 5), [orderHistory]);

  const has = (id) => !tabs.length || tabs.includes(id);
  const d = new Date();
  const today = `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日 星期${WEEK_ZH[d.getDay()]}`;

  const card = { background: '#fff', borderRadius: 14, padding: '16px 18px', marginBottom: 14, boxShadow: '0 1px 6px rgba(0,0,0,0.05)' };
  const btn = (bg, color, border) => ({ padding: '8px 14px', borderRadius: 9, border: `1.5px solid ${border || bg}`, background: bg, color,
                                        fontSize: 13, fontWeight: 800, cursor: 'pointer' });

  return (
    <div data-staff-home style={{ flex: 1, overflowY: 'auto', background: '#f0f2f5' }}>
      <div style={{ maxWidth: 880, margin: '0 auto', padding: '20px 16px 40px' }}>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 22, fontWeight: 900, color: '#18181B' }}>你好！{storeName ? ` · ${storeName}` : ''}</div>
          <div style={{ fontSize: 13, color: '#71717A', marginTop: 2 }}>{today}</div>
        </div>

        {/* 订货提醒 */}
        {has('inventory') && (
          <div data-staff-recs style={card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: recs.length ? 10 : 0 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#18181B', flex: 1 }}>
                {recs.length ? `🚨 需要订货 ${recs.length} 项` : '✅ 库存充足，暂时不用订货'}
              </div>
              {recs.length > 0 && onAddToPurchaseOrder && has('purchase') && (
                <button type="button" data-staff-recs-add
                  onClick={() => onAddToPurchaseOrder(Object.fromEntries(recs.map(r => [r.name, r.recommendedQty])))}
                  style={btn('#FF6F0F', '#fff')}>📤 全部加到订货单</button>
              )}
              <button type="button" onClick={() => onNavigate && onNavigate('inventory')} style={btn('#fff', '#18181B', '#D4D4D8')}>库存管理 →</button>
            </div>
            {recs.length === 0 && (
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>在「库存管理」里填写现有库存后，这里会提醒该订货的食材。</div>
            )}
            {recs.map(r => (
              <div key={r.name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderTop: '1px solid #f3f4f6', fontSize: 13 }}>
                <span>{r.status.icon}</span>
                <span style={{ flex: 1, fontWeight: 700, color: '#18181B' }}>{zhName(r.name)}</span>
                <span style={{ color: '#71717A' }}>现有 {r.currentStock}{unitToZh(r.unit) || r.unit}</span>
                <span style={{ color: '#EA580C', fontWeight: 800 }}>建议 {r.recommendedQty}{unitToZh(r.unit) || r.unit}</span>
              </div>
            ))}
          </div>
        )}

        {/* V149 今日工作 — 오늘 목록이 있으면 진행 상황 */}
        {has('work') && Array.isArray(workToday?.items) && workToday.items.length > 0 && (() => {
          const total = workToday.items.length;
          const done = workToday.items.filter(i => workChecks?.[i.id]?.done).length;
          return (
            <button type="button" data-staff-work onClick={() => onNavigate && onNavigate('work')}
              style={{ ...card, width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 24 }}>{done === total ? '🎉' : '✅'}</span>
              <span style={{ flex: 1 }}>
                <span style={{ display: 'block', fontSize: 15, fontWeight: 800, color: '#18181B' }}>今日工作 · 已完成 {done} / {total}</span>
                <span style={{ display: 'block', height: 6, background: '#F4F4F5', borderRadius: 999, marginTop: 6, overflow: 'hidden' }}>
                  <span style={{ display: 'block', width: `${Math.round(done / total * 100)}%`, height: '100%', background: done === total ? '#16A34A' : '#FF6F0F' }} />
                </span>
              </span>
              <span style={{ fontSize: 13, fontWeight: 800, color: '#52525B' }}>打开 →</span>
            </button>
          );
        })()}

        {/* 快捷入口 */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10, marginBottom: 14 }}>
          {TILES.filter(tl => has(tl.id)).map(tl => (
            <button key={tl.id} type="button" data-staff-tile={tl.id} onClick={() => onNavigate && onNavigate(tl.id)}
              style={{ ...card, marginBottom: 0, textAlign: 'left', border: 'none', cursor: 'pointer' }}>
              <div style={{ fontSize: 26 }}>{tl.icon}</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#18181B', marginTop: 4 }}>{tl.label}</div>
              <div style={{ fontSize: 12, color: '#71717A', marginTop: 2 }}>{tl.desc}</div>
            </button>
          ))}
        </div>

        {/* 最近的订货单 */}
        {has('purchase') && (
          <div style={card}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#18181B', flex: 1 }}>📅 最近的订货单</div>
              <button type="button" onClick={() => onNavigate && onNavigate('purchase')} style={btn('#fff', '#18181B', '#D4D4D8')}>订货单 →</button>
            </div>
            {recent.length === 0 && <div style={{ fontSize: 12, color: '#9ca3af' }}>还没有保存的订货单。</div>}
            {recent.map(e => {
              const vs = [...new Set((e.items || []).map(i => String(i.vendor || '').trim()).filter(Boolean))];
              const vLabel = e.vendor ? vName(e.vendor) : vs.length === 1 ? vName(vs[0]) : vs.length > 1 ? `${vName(vs[0])} 等 ${vs.length} 个供应商` : '未指定供应商';
              return (
                <div key={e.id} data-staff-recent style={{ display: 'flex', gap: 10, padding: '7px 0', borderTop: '1px solid #f3f4f6', fontSize: 13 }}>
                  <span style={{ color: '#71717A', minWidth: 84 }}>{e.date}</span>
                  <span style={{ flex: 1, fontWeight: 700, color: '#18181B' }}>🏪 {vLabel}</span>
                  <span style={{ color: '#52525B' }}>{(e.items || []).length} 项</span>
                  <span style={{ color: '#52525B', minWidth: 70, textAlign: 'right' }}>{fmt(e.totalCost || 0)}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* 원래 홈 (한국어) — 운영 권한이 있을 때만 */}
        {has('profit') && onShowFull && (
          <button type="button" data-staff-full onClick={onShowFull}
            style={{ ...btn('#fff', '#52525B', '#E4E4E7'), width: '100%', fontWeight: 700 }}>
            📊 查看经营数据（营业额 · 成本等，部分为韩文）
          </button>
        )}
      </div>
    </div>
  );
}

// 홈 탭 — 중국어 화면이면 직원용 홈, 아니면 원래 홈(Dashboard)
export default function HomeView(props) {
  const { lang } = useLang();
  const [full, setFull] = useState(false);
  if (lang === 'zh' && !full) {
    return <StaffHome {...props} onShowFull={() => setFull(true)} />;
  }
  return (
    <>
      {lang === 'zh' && (
        <div style={{ padding: '10px 16px', background: '#fff', borderBottom: '1px solid #e5e7eb' }}>
          <button type="button" onClick={() => setFull(false)}
            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #D4D4D8', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            ← 返回工作台
          </button>
        </div>
      )}
      <Dashboard {...props} />
    </>
  );
}
